import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/nextjs';
import { isEqual, sortBy } from 'lodash';
import { FileKindEnum } from 'scribe-web-shared/enums/file-kind-enums';
import documentMetadataService from 'services/documentMetadataService';
import { haveSameVisibility, suggestGlobalPermissionAfterCopyLink } from 'utils/documentPermissionHelpers';
import { SentryBreadcrumb } from 'utils/SentryHelper';
import { trackEvent } from 'utils/tracking';

import knowledgePageService from '../services/knowledgePageService';
import scribeFileService from '../services/scribeFileService';
import { removeApplicationTag } from './actions/documentMetadataActions';
import { scribeFileUpdateActions } from './actionsSlice';

export const setScribeFileData = createAction('scribe/setScribeFileData');

export const getScribeFile = createAsyncThunk('scribe/getScribeFile', async ({ id, includeActionsCount = false }, { rejectWithValue }) => {
  try {
    return await scribeFileService.getScribeFile({ id, includeActionsCount });
  } catch (err) {
    SentryBreadcrumb.error({
      category: 'getScribeFile',
      message: err,
      data: { id, includeActionsCount }
    });
    Sentry.captureException(err);
    return rejectWithValue(err);
  }
});

export const getScribeFileInBackground = createAsyncThunk('scribe/getScribeFileInBackground', async (id) => {
  const scribeFile = await scribeFileService.getScribeFile({ id });

  return scribeFile;
});

// get scribe file reminder updates whenever document is updated
export const getScribeFileReminderUpdates = createAsyncThunk('scribe/getScribeFileReminderUpdates', async (scribeFile) => {
  let updatedFile = null;

  if (scribeFile?.id && scribeFile?.organization_owner?.show_update_reminder) {
    updatedFile = await scribeFileService.getScribeFile({ id: scribeFile.id });
  }
  return updatedFile;
});

export const getOutline = createAsyncThunk('scribe/getOutline', async ({ id, omitComments }) => {
  const outline = await scribeFileService.getOutline(id, omitComments);

  return outline;
});

export const getOutlineInBackground = createAsyncThunk('scribe/getOutlineInBackground', async ({ id, omitComments }) => {
  const outline = await scribeFileService.getOutline(id, omitComments);

  return outline;
});

// update scribe file
export const updateScribeFile = createAsyncThunk('scribe/updateFile', async ({ documentId, bodyParams }) => {
  const updatedFile = await scribeFileService.updateScribeFile(documentId, bodyParams);

  return updatedFile;
});

const UNREVIEWED_TAGS = ['notreviewed'];

export function toggleTag(currentTags, tag) {
  if (isEqual(currentTags, UNREVIEWED_TAGS)) {
    return [tag];
  }
  if (currentTags.includes(tag)) {
    if (currentTags.length === 1) {
      // The flag we want to toggle is the only tag, so change the Scribe
      // back to unreviewed.
      return UNREVIEWED_TAGS;
    }
    return currentTags.filter((t) => t !== tag);
  }
  return currentTags.concat(tag);
}

export const VERIFIED_TAG = 'bestpractice';
export const UNDER_REVIEW_TAG = 'tobeoptimized';

// update scribe file metadata
export const updateDocumentMetadata = createAsyncThunk('scribe/updateDocumentMetadata', async ({ id, bodyParams }) => {
  return scribeFileService.updateDocumentMetadata({ id, bodyParams });
});

// update file permission
export const updateFilePermission = createAsyncThunk('scribe/updateFilePermission', async (data) => {
  const { document, bodyParams } = data;

  if (document?.type === 'knowledge_page') {
    trackEvent(`knowledge_page_permission_update_${bodyParams.global_permission}`);
    return knowledgePageService.updateKnowledgePagePermission(document?.id, bodyParams);
  }
  return scribeFileService.updateScribeFile(document?.id, bodyParams);
});

export const adjustGlobalPermissionAfterCopyLink = createAsyncThunk(
  'scribe/adjustGlobalPermissionAfterCopyLink',
  async ({ document, permissions }, { getState }) => {
    const state = getState();
    const { scribeFile } = state;
    const { usersWithAccess, candidates } = scribeFile;
    const sharedWithIndividuals = usersWithAccess.length + candidates.length > 0;

    const suggestedPermission = suggestGlobalPermissionAfterCopyLink(document, permissions, sharedWithIndividuals);

    if (!suggestedPermission) return;
    const { id } = document;
    const body = { global_permission: suggestedPermission };
    const currentPermission = document.global_permission;

    if (!haveSameVisibility(currentPermission, suggestedPermission)) {
      body.folder_id = null;
    }
    if (document.type === 'knowledge_page') {
      trackEvent(`knowledge_page_permission_update_${suggestedPermission}`);
    }
    return documentMetadataService.updateDocumentMetadata(id, body);
  }
);

export const addActionToOutlineAndSort = (outline, action) => {
  const newOutline = [...outline];

  newOutline.push(action);
  return sortBy(newOutline, ['sort_order']);
};

export const addUserFilePermission = createAsyncThunk('scribe/addUserDocumentPermission', async ({ id, userId, permissionLevel }) => {
  const user = await scribeFileService.changeUserPermission(id, userId, permissionLevel);

  return user;
});

export const addCandidateFilePermission = createAsyncThunk(
  'scribe/addCandidateFilePermission',
  async ({ id, email, permissionLevel }, { rejectWithValue }) => {
    try {
      const user = await scribeFileService.changeCandidatePermission(id, email, permissionLevel);

      return user;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getUsersWithPermission = createAsyncThunk('scribe/getUsersWithPermission', async (id) => {
  const users = await scribeFileService.getUsersWithPermission(id);

  return users;
});

export const getCandidatesWithPermission = createAsyncThunk('scribe/getCandidatesWithPermission', async (id) => {
  const users = await scribeFileService.getCandidatesWithPermission(id);

  return users;
});

export const getDocumentEstimatedTime = createAsyncThunk('scribe/getDuration', async (id) => {
  const users = await scribeFileService.getDocumentEstimatedTime(id);

  return users;
});

export const requestAccess = createAsyncThunk('scribe/requestAccess', async (id) => {
  return scribeFileService.requestAccess(id);
});

export const requestSuperOrganizationSSOInformation = createAsyncThunk('scribe/requestSuperOrganizationSSOInformation', async (id) => {
  return scribeFileService.requestSuperOrganizationSSOInformation(id);
});

export const INITIAL_STATE = {
  getOutlineRequestId: null,
  data: null,
  isLoading: false,
  isError: false,
  isUpdating: false,
  isUpdateError: null,
  errorMessage: null,
  usersWithAccess: [],
  candidates: [],
  isLoadingUsersWithAccess: false,
  loadingUsersWithAccessError: null,
  awaitingUsersToSignUp: [],
  outline: []
};

const scribeFileSlice = createSlice({
  name: 'scribeFileSlice',
  initialState: { ...INITIAL_STATE },
  reducers: {
    updateScribeFileLocally: (state, action) => {
      state.data = action.payload;
      state.isUpdating = false;
      state.isUpdateError = false;
      state.errorMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setScribeFileData, (state, action) => {
        const { data, error } = action.payload;

        return {
          ...INITIAL_STATE,
          data,
          outline: data.outline || [],
          isError: !!error
        };
      })
      .addCase(getScribeFile.fulfilled, (state, action) => {
        const fileData = { ...action.payload };

        state.data = fileData;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getScribeFile.pending, (state) => {
        state.data = null;
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getScribeFile.rejected, (state, action) => {
        state.data = null;
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getScribeFileInBackground.fulfilled, (state, action) => {
        const fileData = { ...action.payload };

        state.data = fileData;
      })
      .addCase(getScribeFileInBackground.pending, (state) => {})
      .addCase(getScribeFileInBackground.rejected, (state, action) => {})
      .addCase(getScribeFileReminderUpdates.fulfilled, (state, action) => {
        if (action.payload) {
          const fileData = { ...action.payload };

          state.data = fileData;
        }
      })
      .addCase(getOutline.fulfilled, (state, action) => {
        const { id } = action.meta.arg;

        if (id === state.getOutlineRequestId) {
          if (state.data) {
            state.data.outline = action.payload;
          }
          state.outline = action.payload;
          state.getOutlineRequestId = null;
          state.isLoading = false;
          state.isError = false;
          state.errorMessage = null;
        }
      })
      .addCase(getOutline.pending, (state, action) => {
        const { id } = action.meta.arg;

        state.getOutlineRequestId = id;
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getOutline.rejected, (state, action) => {
        state.getOutlineRequestId = null;
        state.outline = [];
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getOutlineInBackground.fulfilled, (state, action) => {
        const { id } = action.meta.arg;

        if (id === state.getOutlineRequestId) {
          if (state.data) {
            state.data.outline = action.payload;
          }
          state.outline = action.payload;
          state.getOutlineRequestId = null;
          state.isLoading = false;
          state.isError = false;
          state.errorMessage = null;
        }
      })
      .addCase(getOutlineInBackground.pending, (state, action) => {
        const { id } = action.meta.arg;

        state.getOutlineRequestId = id;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getOutlineInBackground.rejected, (state, action) => {
        state.getOutlineRequestId = null;
        state.outline = [];
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(updateScribeFile.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isUpdating = false;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateScribeFile.pending, (state, action) => {
        const { bodyParams } = action.meta.arg;
        const iconColor = bodyParams?.icon_color;

        state.oldColor = state.data.icon_color;
        if (iconColor) {
          state.data = { ...state.data, icon_color: iconColor };
        }
        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateScribeFile.rejected, (state, action) => {
        state.data = { ...state.data, icon_color: state.oldColor };
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase(updateDocumentMetadata.fulfilled, (state, action) => {
        state.data = { ...state.data, ...action.payload };
        state.isUpdating = false;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateDocumentMetadata.pending, (state, action) => {
        const { bodyParams } = action.meta.arg;

        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateDocumentMetadata.rejected, (state, action) => {
        state.data = { ...state.data };
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase(updateFilePermission.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isUpdating = false;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateFilePermission.pending, (state) => {
        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(updateFilePermission.rejected, (state, action) => {
        state.data = null;
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase(scribeFileUpdateActions, (state, action) => {
        const { updatedActions } = action.payload;
        const updatedOutline = [];

        updatedActions.forEach((updatedAction, index) => {
          if (updatedAction.kind === 'section') {
            updatedOutline.push(updatedAction);
          } else if (updatedAction?.comments?.length >= 1) {
            updatedOutline.push({ ...updatedAction, num_comments: updatedAction.comments.length });
          }
        });
        state.outline = updatedOutline;
        if (state.data) {
          state.data.outline = updatedOutline;
        } else {
          state.data = {
            outline: updatedOutline
          };
        }
      })
      .addCase(addUserFilePermission.fulfilled, (state, action) => {
        const { permissionLevel, userId } = action.meta.arg;

        if (permissionLevel === 'none') {
          //  remove user from usersWithAccess
          state.usersWithAccess = state.usersWithAccess.filter((user) => user.user.id !== userId);
        } else {
          // add user to usersWithAccess
          const updatedUserPermission = action.payload?.permission_level === 200 ? 'write' : 'read';
          const updatedUserWithPermission = { user: action.payload.user, permission_level: updatedUserPermission };
          const usersWithAccessWithUpdatedUser = state.usersWithAccess.find((user) => user.user.id === updatedUserWithPermission.user.id);

          state.usersWithAccess = usersWithAccessWithUpdatedUser
            ? state.usersWithAccess.map((user) => (user.user.id === updatedUserWithPermission.user.id ? updatedUserWithPermission : user))
            : [updatedUserWithPermission, ...state.usersWithAccess];
        }
        state.isUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addUserFilePermission.pending, (state) => {
        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(addUserFilePermission.rejected, (state, action) => {
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase(addCandidateFilePermission.fulfilled, (state, action) => {
        const { permissionLevel, email } = action.meta.arg;

        if (permissionLevel === 'none') {
          //  remove user from candidates
          state.candidates = state.candidates.filter((user) => user.email !== email);
        } else {
          // add user with scribe account to users with access as individually shared user
          const isUserWithScribeAccountAddedAsCandidate = action?.payload?.user;

          if (isUserWithScribeAccountAddedAsCandidate) {
            state.usersWithAccess = [
              { user: action.payload.user, permission_level: action.payload?.permission_level === 200 ? 'write' : 'read' },
              ...state.usersWithAccess
            ];
          } else {
            // add user to candidates
            const updatedCandidatePermission = action.payload;
            const candidatesWithExistingCandidate = state.candidates.find((user) => user.email === updatedCandidatePermission.email);

            state.candidates = candidatesWithExistingCandidate
              ? state.candidates.map(
                  (user) => (user.email === updatedCandidatePermission.email ? updatedCandidatePermission : user) // update existing candidate permission
                )
              : [updatedCandidatePermission, ...state.candidates]; // add new candidate
          }
        }
        state.isUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addCandidateFilePermission.pending, (state) => {
        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(addCandidateFilePermission.rejected, (state, action) => {
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getUsersWithPermission.fulfilled, (state, action) => {
        const usersWithPermission =
          action.payload?.map((user) => ({ ...user, permission_level: user.permission_level === 200 ? 'write' : 'read' })) || [];

        state.usersWithAccess = usersWithPermission;
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getUsersWithPermission.pending, (state) => {
        state.isLoadingUsersWithAccess = true;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getUsersWithPermission.rejected, (state, action) => {
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getCandidatesWithPermission.fulfilled, (state, action) => {
        state.candidates = action.payload;
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getCandidatesWithPermission.pending, (state) => {
        state.isLoadingUsersWithAccess = true;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getCandidatesWithPermission.rejected, (state, action) => {
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getDocumentEstimatedTime.fulfilled, (state, action) => {
        state.data = {
          ...state.data,
          estimated_time: action.payload?.estimated_time || state.data?.estimated_time
        };
      })
      .addCase(getDocumentEstimatedTime.pending, (state) => {})
      .addCase(getDocumentEstimatedTime.rejected, (state, action) => {})
      .addCase(removeApplicationTag.fulfilled, (state, action) => {
        const { appTagId, type } = action.meta.arg;

        if (type === FileKindEnum.Scribe) {
          state.data.app_tags = state.data.app_tags.filter((tag) => tag.id !== appTagId);
        }
      })
      .addCase('signOutGlobally', (state) => ({ ...INITIAL_STATE }));
  }
});

export const selectScribeFile = createSelector(
  [
    (state) => state.scribeFile.data,
    (state) => state.scribeFile.outline,
    (state) => state.scribeFile.isError,
    (state) => state.scribeFile.isLoading,
    (state) => state.scribeFile.isUpdating,
    (state) => state.scribeFile.isUpdateError
  ],
  /**
   * @returns {ScribeFileStateType}
   */
  (scribeFile, outline, isError, isLoading, isUpdating, isUpdateError) => ({
    scribeFile,
    outline,
    isError,
    isLoading,
    isUpdating,
    isUpdateError
  })
);

export const { updateScribeFileLocally } = scribeFileSlice.actions;
export default scribeFileSlice.reducer;
